import { useMemo, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { Grid2, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { TextInput, SelectComponent, AlertDialog } from '@utriper/core-ui';
import { Reseller } from '@utriper/core';
import { useTranslation } from 'core/contexts/localization';

const ENVIRONMENT_OPTIONS = [
    { id: 'live', name: 'Live' },
    { id: 'test', name: 'Test' },
];

const ApiKeyModal = ({ open, onClose, onSubmit, loading = false }) => {
    const { t: translate, i18n } = useTranslation();
    const i18nLang = i18n.language;
    const [formError, setFormError] = useState(null);

    const resellerOptions = useMemo(
        () => [
            ...Reseller?.getIds().map((id) => {
                const translated = Reseller.getTranslatedName(Reseller.types, id, i18nLang);
                return { name: translated?.[0] ?? '', id };
            }),
        ],
        [i18n.language]
    );

    const handleSubmit = async (values) => {
        try {
            setFormError(null);

            // Validate required fields
            if (!values.name || !values.name.trim()) {
                setFormError(translate('experiencer.apiKey.errors.nameRequired') || 'API Key name is required');
                return;
            }

            if (!values.resellerId) {
                setFormError(translate('experiencer.apiKey.errors.resellerRequired') || 'Reseller type is required');
                return;
            }

            await onSubmit(values);
            onClose();
        } catch (error) {
            setFormError(error.message || translate('experiencer.apiKey.errors.unknown') || 'Failed to create API key');
        }
    };

    const validate = (values) => {
        const errors = {};

        if (!values.name || !values.name.trim()) {
            errors.name = translate('experiencer.apiKey.errors.nameRequired') || 'Required';
        }

        if (!values.resellerId) {
            errors.resellerId = translate('experiencer.apiKey.errors.resellerRequired') || 'Required';
        }

        return errors;
    };

    return (
        <AlertDialog
            open={open}
            handleClose={onClose}
            hideActions
            maxWidth="sm"
            fullWidth
            dialogTitle={translate('experiencer.apiKey.title', 'Add API Key')}
            dialogContent={
                <Form
                    onSubmit={handleSubmit}
                    validate={validate}
                    initialValues={{
                        name: '',
                        resellerId: '',
                        environment: 'live',
                    }}
                    render={({ handleSubmit, submitting, pristine }) => (
                        <form onSubmit={handleSubmit}>
                            <DialogContent>
                                <Grid2 container spacing={2}>
                                    {formError && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <div
                                                style={{
                                                    padding: '12px',
                                                    backgroundColor: '#fee',
                                                    border: '1px solid #fcc',
                                                    borderRadius: '4px',
                                                    color: '#c33',
                                                }}>
                                                {formError}
                                            </div>
                                        </Grid2>
                                    )}

                                    <Grid2 size={{ xs: 12 }}>
                                        <Field name="name" component={TextInput} label={translate('experiencer.apiKey.name') || 'API Key Name'} placeholder={translate('experiencer.apiKey.namePlaceholder') || 'e.g., Production Viator Key'} fullWidth required />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <Field id="resellerId" name="resellerId" component={SelectComponent} values={resellerOptions} label={translate('experiencer.apiKey.resellerType') || 'Reseller Type'} labelId="reseller-type-label" style={{ width: '100%' }} required />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12 }}>
                                        <Field id="environment" name="environment" component={SelectComponent} values={ENVIRONMENT_OPTIONS} label={translate('experiencer.apiKey.environment') || 'Environment'} labelId="environment-label" style={{ width: '100%' }} />
                                    </Grid2>
                                </Grid2>
                            </DialogContent>

                            <DialogActions>
                                <Button onClick={onClose} disabled={submitting || loading}>
                                    {translate('btn_cancel') || 'Cancel'}
                                </Button>
                                <Button type="submit" variant="contained" color="primary" disabled={submitting || loading || pristine} className="triper-button bg-green-300">
                                    {loading ? translate('btn_loading') || 'Creating...' : translate('btn_submit') || 'Create'}
                                </Button>
                            </DialogActions>
                        </form>
                    )}
                />
            }
        />
    );
};

ApiKeyModal.displayName = 'ApiKeyModal';

export default ApiKeyModal;
